const UUID_BYTE_LENGTH = 16;

/**
 * Installs `crypto.randomUUID` where the browser lacks it.
 *
 * The OneSignal web SDK is loaded from the provider's CDN and calls
 * `crypto.randomUUID()` without a guard while it initializes. The method is
 * missing in browsers older than Chrome 92, Safari 15.4 and Firefox 95, and in
 * pages that are not a secure context, which includes some in-app webviews.
 * There the call threw and push setup failed with a TypeError.
 *
 * Builds a version 4 UUID (RFC 4122) from `crypto.getRandomValues`, which is
 * available far more widely than `randomUUID` and outside secure contexts. It
 * does nothing when `randomUUID` already exists, and nothing when there is no
 * `crypto.getRandomValues` to build from: a missing source of randomness must
 * not turn into a startup crash, and a weaker `Math.random` fallback would
 * hand out identifiers that look unique and are not.
 */
export function installRandomUUIDPolyfill() {
  const cryptoObject = globalThis.crypto;

  if (!cryptoObject || typeof cryptoObject.randomUUID === 'function') {
    return;
  }

  if (typeof cryptoObject.getRandomValues !== 'function') {
    return;
  }

  const randomUUID = () => {
    const bytes = cryptoObject.getRandomValues(new Uint8Array(UUID_BYTE_LENGTH));

    // Version 4 in the high nibble of byte 6, variant 10xx in byte 8.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;

    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));

    return [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10).join(''),
    ].join('-') as `${string}-${string}-${string}-${string}-${string}`;
  };

  try {
    Object.defineProperty(cryptoObject, 'randomUUID', {
      value: randomUUID,
      configurable: true,
      writable: true,
    });
  } catch {
    // A frozen or locked-down crypto object cannot be patched, and push init
    // then fails the way it did before, into the handler that reports it.
  }
}

installRandomUUIDPolyfill();
